'use client';
import { useState } from 'react';

export function PresentationActions() {
  const [updating, setUpdating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [status, setStatus] = useState<string>('');

  const refreshScreenshot = async (): Promise<boolean> => {
    setUpdating(true);
    setStatus('Refreshing visualizer screenshot…');
    try {
      const r = await fetch('/api/refresh-visualizer-screenshot', { method: 'POST' });
      const j = (await r.json()) as { ok: boolean; path?: string; error?: string };
      if (!j.ok) {
        setStatus(`Update failed: ${j.error ?? 'unknown error'}`);
        return false;
      }
      const imgs = document.querySelectorAll<HTMLImageElement>('img[src^="/visualizer-screenshot.png"]');
      imgs.forEach((img) => {
        img.src = `/visualizer-screenshot.png?v=${Date.now()}`;
      });
      setStatus('Visualizer screenshot refreshed.');
      return true;
    } catch (e) {
      setStatus(`Update failed: ${e instanceof Error ? e.message : String(e)}`);
      return false;
    } finally {
      setUpdating(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    setStatus('Building PDF (re-runs the visualizer screenshot then renders the deck)…');
    try {
      const r = await fetch('/api/presentation-pdf');
      if (!r.ok) {
        const j = await r.json().catch(() => ({ error: r.statusText }));
        setStatus(`PDF failed: ${j.error ?? r.statusText}`);
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `triumph-presentation-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setStatus('PDF downloaded.');
    } catch (e) {
      setStatus(`PDF failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setDownloading(false);
    }
  };

  const baseBtn =
    'text-[11px] px-3 py-1.5 rounded border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 hover:border-amber-400/70 transition-colors uppercase tracking-wide font-semibold disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className="flex-shrink-0 flex flex-col items-end gap-2">
      <div className="flex items-center gap-2">
        <button
          onClick={refreshScreenshot}
          disabled={updating || downloading}
          className={baseBtn}
          title="Re-capture the visualizer 3D scatter screenshot used on slide 11. Slide 12 already pulls live data on every load."
        >
          {updating ? 'Updating…' : 'Update'}
        </button>
        <button
          onClick={downloadPdf}
          disabled={updating || downloading}
          className={baseBtn}
          title="Refresh slide 11 then build a 16:9 PDF of the full deck."
        >
          {downloading ? 'Building…' : 'Download PDF'}
        </button>
        <a
          href="/api/presentation-pptx"
          target="_blank"
          rel="noopener noreferrer"
          className={baseBtn}
          title="Generate a fresh PPTX (15 slides, 16:9) via build_v15.py — opens in a new tab."
        >
          Generate PPTX
        </a>
        <a
          href="/presentation/live"
          className={baseBtn}
          title="Live presentation walk — 9 steps designed for actual client demos, with arrow-key nav + open-live buttons to each surface."
        >
          ▶ Live walk
        </a>
      </div>
      {status && <div className="text-[10px] text-[#94a3b8] italic">{status}</div>}
    </div>
  );
}
